import type { FeatureFlagConfiguration } from "@spiriidk/feature-flags";

/**
 * Well-known localStorage key holding per-flag `defaultVariant` overrides as a
 * JSON object (e.g. `{ "release.connect.settings": "on" }`). Playwright sets it
 * per project via `addInitScript` so a project can serve `/settings` from either
 * the legacy or the migrated implementation without a rebuild (adr/0004, design
 * D5). Read only under the mock flag — see {@link resolveMockFlagConfig}.
 */
const FLAG_OVERRIDES_STORAGE_KEY = "e2e:flag-overrides";

type FlagOverrides = Record<string, string>;

/**
 * Parse the override map from storage. Returns `{}` when the key is absent or
 * the value is not a JSON object of string variants, so a malformed override can
 * never crash boot.
 */
function readFlagOverrides(storage: Pick<Storage, "getItem">): FlagOverrides {
  const raw = storage.getItem(FLAG_OVERRIDES_STORAGE_KEY);
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};

    const overrides: FlagOverrides = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "string") overrides[key] = value;
    }
    return overrides;
  } catch {
    return {};
  }
}

/**
 * Return a copy of `config` with each flag's `defaultVariant` replaced by its
 * override, where one is provided for a flag that exists in the config. Mirrors
 * `@spiriidk/feature-flags`'s `setupTestFlag`, but per-flag rather than one
 * blanket variant. Flags without an override are left untouched.
 *
 * An override is applied only when its value is a real variant of that flag. An
 * unknown variant (e.g. stale localStorage after a variant rename) is ignored,
 * because it would otherwise become an invalid `defaultVariant` whose
 * `variants[defaultVariant]` is `undefined` — and `getFlag`'s `getFlagValue`
 * throws "Unsupported flag type" on that, crashing router boot in mock mode.
 * Keeping the config untouched preserves the "a malformed override can never
 * crash boot" contract.
 */
function mergeFlagOverrides(
  config: FeatureFlagConfiguration,
  overrides: FlagOverrides
): FeatureFlagConfiguration {
  const merged: FeatureFlagConfiguration = {};
  for (const [key, flag] of Object.entries(config)) {
    const variant = overrides[key];
    merged[key] =
      variant && variant in flag.variants
        ? { ...flag, defaultVariant: variant }
        : flag;
  }
  return merged;
}

interface ResolveMockFlagConfigArgs {
  isMockApiEnabled: boolean;
  config: FeatureFlagConfiguration;
  storage: Pick<Storage, "getItem">;
}

/**
 * Resolve the flag config passed to `initializeFeatureFlags`: under the mock
 * flag, merge the localStorage overrides on top of the config defaults;
 * otherwise return the config untouched (the override seam is inert in a real
 * deployment).
 */
function resolveMockFlagConfig({
  isMockApiEnabled,
  config,
  storage,
}: ResolveMockFlagConfigArgs): FeatureFlagConfiguration {
  if (!isMockApiEnabled) return config;
  return mergeFlagOverrides(config, readFlagOverrides(storage));
}

// `mergeFlagOverrides` is intentionally module-local — only `resolveMockFlagConfig`
// (and the colocated tests, via it) consume it.
export { FLAG_OVERRIDES_STORAGE_KEY, readFlagOverrides, resolveMockFlagConfig };
export type { FlagOverrides };
